import { html, attr, on, Fragment, MapSignal, type Signal } from '@tempots/dom'
import type { PageMode, PageSizeKey } from '@rh/shared/schemas/design'
import type { DesignEditorState } from './design-state'
import type { DesignActions } from './design-actions'
import { GRID_COLS, type GridSizeKey } from './design-state'
import { WATERMARK_ID } from './design-state'

// ============================================================
// LEAVE CONFIRM DIALOG — shown when navigating away with unsaved
// changes (see unsaved-changes-guard.ts).
// ============================================================

export interface LeaveConfirmDialogOptions {
  open: Signal<boolean>
  saving: Signal<boolean>
  onSaveAndLeave: () => void
  onLeave: () => void
  onStay: () => void
}

export function LeaveConfirmDialog({
  open,
  saving,
  onSaveAndLeave,
  onLeave,
  onStay,
}: LeaveConfirmDialogOptions) {
  return MapSignal(open, isOpen => {
    if (!isOpen) return html.span()
    return html.div(
      attr.class('leave-dialog-backdrop'),
      // Clicking the backdrop (not the dialog itself) means "stay".
      on.click((e: MouseEvent) => {
        if (e.target === e.currentTarget) onStay()
      }),
      html.div(
        attr.class('leave-dialog'),
        html.div(attr.class('leave-dialog-title'), 'Unsaved design changes'),
        html.div(
          attr.class('leave-dialog-body'),
          "Your design has changes that haven't been saved."
        ),
        html.button(
          attr.class('btn btn-primary'),
          attr.disabled(saving),
          on.click(onSaveAndLeave),
          saving.map((s): string => (s ? 'Saving…' : 'Save and leave'))
        ),
        html.button(
          attr.class('btn btn-secondary'),
          on.click(onLeave),
          'Leave without saving'
        ),
        html.button(attr.class('btn btn-secondary'), on.click(onStay), 'Stay')
      )
    )
  })
}

// ============================================================
// PAGE TABS — shown above the canvas in firstAndSubsequent mode
// ============================================================

export function PageTabs(state: DesignEditorState, actions: DesignActions) {
  const switchTab = (tab: 'first' | 'subsequent') => actions.switchTab(tab)
  return MapSignal(state.pageMode, mode => {
    if (mode !== 'firstAndSubsequent') return html.span()
    return MapSignal(state.activeTab, tab =>
      html.div(
        attr.style('display:flex; gap:0; margin-bottom:0;'),
        html.div(
          attr.style(
            [
              'padding:8px 20px; cursor:pointer; font-size:13px; font-weight:600; border-radius:6px 6px 0 0;',
              tab === 'first'
                ? 'background:white; color:#1e1e1e; box-shadow:0 -2px 6px rgba(0,0,0,0.08);'
                : 'background:#c8c8c8; color:#666;',
            ].join(' ')
          ),
          on.click((e: MouseEvent) => {
            e.stopPropagation()
            switchTab('first')
          }),
          'First Page'
        ),
        html.div(
          attr.style(
            [
              'padding:8px 20px; cursor:pointer; font-size:13px; font-weight:600; border-radius:6px 6px 0 0;',
              tab === 'subsequent'
                ? 'background:white; color:#1e1e1e; box-shadow:0 -2px 6px rgba(0,0,0,0.08);'
                : 'background:#c8c8c8; color:#666;',
            ].join(' ')
          ),
          on.click((e: MouseEvent) => {
            e.stopPropagation()
            switchTab('subsequent')
          }),
          'Subsequent Pages'
        )
      )
    )
  })
}

// ============================================================
// CONTROL PANEL
// ============================================================

export interface ControlPanelOptions {
  state: DesignEditorState
  actions: DesignActions
  onSave: () => void
  onTour: () => void
  onOpenWizard: () => void
}

export function ControlPanel({
  state,
  actions,
  onSave,
  onTour,
  onOpenWizard,
}: ControlPanelOptions) {
  return Fragment(
    html.div(
      attr.class('control-panel'),

      // 1. History
      html.div(
        attr.class('ctrl-section'),
        html.div(attr.class('ctrl-label'), 'History'),
        html.div(
          attr.class('btn-row'),
          html.button(
            attr.class('btn btn-secondary'),
            on.click(() => actions.undo()),
            '↩ Undo'
          ),
          html.button(
            attr.class('btn btn-secondary'),
            on.click(() => actions.redo()),
            '↪ Redo'
          )
        )
      ),

      // 2. Add Component
      html.div(
        attr.class('ctrl-section'),
        html.div(attr.class('ctrl-label'), 'Add Component'),
        html.button(
          attr.class('btn btn-primary'),
          on.click(() => {
            actions.addTextBlock()
          }),
          '+ Text Block'
        ),
        html.button(
          attr.class('btn btn-primary'),
          on.click(() => actions.addContainer()),
          '+ Box'
        ),
        html.button(
          attr.class('btn btn-primary'),
          on.click(() => {
            actions.addImage()
          }),
          '+ Image'
        )
      ),

      // 3. Apply To
      html.div(
        attr.class('ctrl-section'),
        attr.style('margin-top:16px;'),
        html.div(attr.class('ctrl-label'), 'Apply Design To'),
        MapSignal(state.pageMode, mode =>
          html.div(
            attr.style('display:flex;flex-direction:column;gap:4px;'),
            html.button(
              attr.class(
                `btn ${mode === 'all' ? 'btn-primary' : 'btn-secondary'}`
              ),
              on.click(() => actions.setPageMode('all' as PageMode)),
              'All Pages'
            ),
            html.button(
              attr.class(
                `btn ${mode === 'firstOnly' ? 'btn-primary' : 'btn-secondary'}`
              ),
              on.click(() => actions.setPageMode('firstOnly' as PageMode)),
              'First Page Only'
            ),
            html.button(
              attr.class(
                `btn ${mode === 'firstAndSubsequent' ? 'btn-primary' : 'btn-secondary'}`
              ),
              on.click(() =>
                actions.setPageMode('firstAndSubsequent' as PageMode)
              ),
              'First & Subsequent Are Different'
            )
          )
        )
      ),

      // 5. Watermark
      html.div(
        attr.class('ctrl-section'),
        attr.style('margin-top:16px;'),
        html.div(attr.class('ctrl-label'), 'Watermark'),
        MapSignal(state.components, comps => {
          const hasWatermark = comps.some(c => c.id === WATERMARK_ID)
          return html.button(
            attr.class(`btn ${hasWatermark ? 'btn-primary' : 'btn-secondary'}`),
            on.click(() => actions.toggleWatermark()),
            hasWatermark ? 'Watermark On' : 'Watermark Off'
          )
        })
      ),

      // 6. Grid
      html.div(
        attr.class('ctrl-section'),
        attr.style('margin-top:16px;'),
        html.div(attr.class('ctrl-label'), 'Grid'),
        MapSignal(state.gridVisible, vis =>
          MapSignal(state.gridSnap, snap =>
            MapSignal(state.gridSize, sz =>
              html.div(
                attr.style('display:flex;flex-direction:column;gap:6px;'),
                // Toggle row: show/hide + snap
                html.div(
                  attr.class('btn-row'),
                  html.button(
                    attr.class(`btn ${vis ? 'btn-primary' : 'btn-secondary'}`),
                    on.click(() =>
                      state.gridVisible.set(!state.gridVisible.value)
                    ),
                    vis ? 'Grid On' : 'Grid Off'
                  ),
                  html.button(
                    attr.class(`btn ${snap ? 'btn-primary' : 'btn-secondary'}`),
                    on.click(() => state.gridSnap.set(!state.gridSnap.value)),
                    snap ? 'Snap On' : 'Snap Off'
                  )
                ),
                // Size row
                html.div(
                  attr.class('btn-row'),
                  ...(Object.keys(GRID_COLS) as GridSizeKey[]).map(key =>
                    html.button(
                      attr.class(
                        `btn ${sz === key ? 'btn-primary' : 'btn-secondary'}`
                      ),
                      attr.style('flex:1;min-width:0;padding:0;'),
                      on.click(() => state.gridSize.set(key)),
                      key.toUpperCase()
                    )
                  )
                )
              )
            )
          )
        )
      ),

      // 7. Page Size
      html.div(
        attr.class('ctrl-section'),
        html.div(attr.class('ctrl-label'), 'Page Size'),
        MapSignal(state.pageSize, current =>
          html.div(
            attr.class('btn-row'),
            html.button(
              attr.class(
                `btn ${current === 'letter' ? 'btn-primary' : 'btn-secondary'}`
              ),
              on.click(() => actions.setPageSize('letter' as PageSizeKey)),
              'Letter'
            ),
            html.button(
              attr.class(
                `btn ${current === 'a4' ? 'btn-primary' : 'btn-secondary'}`
              ),
              on.click(() => actions.setPageSize('a4' as PageSizeKey)),
              'A4'
            )
          )
        )
      ),

      // Guided setup + tour
      html.div(
        attr.class('ctrl-section'),
        attr.style('margin-top:16px;'),
        html.div(attr.class('ctrl-label'), 'Need a hand?'),
        html.button(
          attr.class('btn btn-secondary'),
          on.click(onOpenWizard),
          '✨ Set up my header'
        )
      ),
      html.div(
        attr.style('padding:12px 0 4px; text-align:center;'),
        html.button(
          attr.class('btn btn-secondary'),
          attr.style('font-size:11px; color:#9ca3af;'),
          on.click(onTour),
          '? Take the tour'
        )
      )
    ),

    // Save bar — pinned to the bottom of the side panel (persists the
    // design via the API)
    html.div(
      attr.class('save-bar'),
      MapSignal(state.saveStatus, status =>
        status === 'idle'
          ? html.span()
          : html.div(
              attr.class('save-toast'),
              status === 'error'
                ? attr.style(
                    'background:#fee2e2;border-color:#dc2626;color:#b91c1c;'
                  )
                : attr.style(''),
              status === 'saving'
                ? 'Saving…'
                : status === 'saved'
                  ? 'Design saved'
                  : 'Failed to save — try again.'
            )
      ),
      html.button(
        attr.class('btn btn-primary btn-save'),
        attr.disabled(state.saveStatus.map(s => s === 'saving')),
        on.click(onSave),
        'Save Design'
      )
    )
  )
}
